import type {
  ActiveDecision,
  BudgetAdjustment,
  ClusterSpendSummary,
  EvidenceChunkIssue,
  FamilyEstimate,
  FamilyOverview,
  FamilyRunAccepted,
  FamilyRunProgress,
  FamilySettings,
  GapSearchItem,
  InitiativeMention,
  LeadGenerationEstimate,
  LeadGenerationProgress,
  LeadGenerationRecord,
  LeadProjectSummary,
  LeadSearchItems,
  ProjectLeadOrigin,
  LeadSettings,
  RelatedProjects,
  JobSpendSnapshot,
  MentionCandidate,
  MergeEvent,
  ProjectMergeResult,
  ProjectMergeUndo,
  ProjectParticipant,
  ProjectRecord,
  ProjectRevertResult,
  ProjectRevision,
  ProjectSplitResult,
  ProjectUpdateResult,
  ProjectRoadmap,
  OpportunityHypothesis,
  ResearchObjective,
  ReconciliationDecision,
  ReconciliationRunAccepted,
  ResearchGap,
  ResearchGapSkip,
  RunAllowance,
  SearchQueryRecord,
  SpendStageSummary,
  StageCapReport,
  PossibleDuplicateDecision,
  UnresolvedRouting,
  UnresolvedRoutingResolution,
  Vendor,
  VendorAlias,
  VendorAliasRemoval,
  VendorAliasWrite,
  VendorDetail,
  VendorMergeConflict,
  VendorMergeRecord,
  VendorMergeResult,
  VendorStatusChange,
} from "./types";

const API_BASE = (import.meta.env.VITE_API_BASE_URL || "").replace(/\/$/, "");
const DEFAULT_TIMEOUT_MS = Number(import.meta.env.VITE_API_TIMEOUT_MS || 120_000);
const POLL_INTERVAL_MS = 1500;

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public body?: Record<string, unknown>,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

function url(path: string): string {
  if (path.startsWith("http")) return path;
  const p = path.startsWith("/") ? path : `/${path}`;
  return `${API_BASE}${p}`;
}

export type ApiOptions = {
  timeoutMs?: number;
  signal?: AbortSignal;
};

export async function api<T = Record<string, unknown>>(
  method: string,
  path: string,
  body?: unknown,
  options: ApiOptions = {},
): Promise<T> {
  const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), timeoutMs);
  if (options.signal) {
    if (options.signal.aborted) controller.abort();
    else options.signal.addEventListener("abort", () => controller.abort(), { once: true });
  }
  const signal = controller.signal;

  const headers: Record<string, string> = { "Content-Type": "application/json" };

  const opts: RequestInit = {
    method,
    headers,
    credentials: "include",
    signal,
  };
  if (body !== undefined) opts.body = JSON.stringify(body);

  try {
    const res = await fetch(url(path), opts);
    const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    if (!res.ok || data.ok === false) {
      let err = "";
      if (typeof data.error === "string") err = data.error;
      else if (typeof data.detail === "string") err = data.detail;
      else if (data.detail && typeof data.detail === "object" && !Array.isArray(data.detail)) {
        const d = data.detail as Record<string, unknown>;
        err = typeof d.error === "string" ? d.error : JSON.stringify(d);
      }
      if (!err) err = res.statusText || "Request failed";
      throw new ApiError(err, res.status, data);
    }
    return data as T;
  } catch (e) {
    if (e instanceof DOMException && e.name === "AbortError") {
      throw new ApiError("Request timed out", 408);
    }
    throw e;
  } finally {
    window.clearTimeout(timer);
  }
}

export const apiGet = <T>(path: string, options?: ApiOptions) =>
  api<T>("GET", path, undefined, options);
export const apiPost = <T>(path: string, body?: unknown, options?: ApiOptions) =>
  api<T>("POST", path, body, options);
export const apiPut = <T>(path: string, body?: unknown, options?: ApiOptions) =>
  api<T>("PUT", path, body, options);
export const apiDelete = <T>(path: string, body?: unknown, options?: ApiOptions) =>
  api<T>("DELETE", path, body, options);

export type JobStatus = {
  ok: boolean;
  run_id: string;
  status: string;
  result?: Record<string, unknown>;
  error?: string;
  log_url?: string;
};

export async function pollJob<T extends Record<string, unknown>>(
  runId: string,
  {
    intervalMs = POLL_INTERVAL_MS,
    timeoutMs = 600_000,
    onTick,
  }: {
    intervalMs?: number;
    timeoutMs?: number;
    onTick?: (status: string) => void;
  } = {},
): Promise<T & JobStatus> {
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    const data = await apiGet<T & JobStatus>(`/api/jobs/${encodeURIComponent(runId)}`, {
      timeoutMs: 30_000,
    });
    const status = String(data.status || "");
    onTick?.(status);
    if (status === "ok") return data;
    if (status === "error" || status === "interrupted") {
      throw new ApiError(String(data.error || "Job failed"), 500, data);
    }
    await new Promise((r) => window.setTimeout(r, intervalMs));
  }
  throw new ApiError("Job timed out while waiting for completion", 408);
}

/* ========================================================================== *
 * Project discovery v3 — one thin helper per endpoint.
 *
 * The generic `api`/`apiGet`/`apiPost`/`apiPut`/`apiDelete` exports and
 * `pollJob` above are unchanged and still used directly by existing pages.
 * Everything below is a named wrapper over a route that actually exists in
 * orion1/api/routes/*.py, so a page never has to spell a URL.
 *
 * Errors: every helper throws `ApiError`. Its `status` and `body` carry the
 * route's own detail — 402 (budget exceeded), 409 (feature flag off, gap not
 * admitted, merge conflict) all arrive that way, and a surface that swallows
 * them shows an operator nothing.
 * ========================================================================== */


type QueryValue = string | number | boolean | undefined | null;

/** Build `?a=1&b=2`, dropping empty/undefined values so a blank filter is absent. */
function qs(params: Record<string, QueryValue>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === "") continue;
    search.set(key, String(value));
  }
  const text = search.toString();
  return text ? `?${text}` : "";
}

const seg = (value: string) => encodeURIComponent(String(value ?? "").trim());

/* ---------------------------------------------------------------- vendors -- */

export type VendorListResponse = {
  ok: boolean;
  vendors: Vendor[];
  total: number;
  limit: number;
  offset: number;
  status: string;
};

export const listVendors = (
  params: {
    status?: string;
    search?: string;
    routable?: boolean;
    limit?: number;
    offset?: number;
  } = {},
  options?: ApiOptions,
) => apiGet<VendorListResponse>(`/api/vendors${qs(params)}`, options);

export const getVendor = (vendorId: string, options?: ApiOptions) =>
  apiGet<{ ok: boolean; vendor: VendorDetail }>(`/api/vendors/${seg(vendorId)}`, options);

/** Operator confirmation: status `confirmed` plus routing keywords. */
export const confirmVendor = (vendorId: string, actor = "operator") =>
  apiPost<{ ok: boolean; vendor: VendorStatusChange }>(`/api/vendors/${seg(vendorId)}/confirm`, {
    actor,
  });

/** The only path out of the non-routable state mention extraction creates. */
export const promoteVendor = (vendorId: string, actor = "operator") =>
  apiPost<{ ok: boolean; vendor: VendorStatusChange }>(`/api/vendors/${seg(vendorId)}/promote`, {
    actor,
  });

export const setVendorCtpp = (
  vendorId: string,
  body: {
    eligible?: boolean | null;
    parent_name?: string;
    provenance?: string;
    confirm?: boolean;
    actor?: string;
  },
) => apiPut<{ ok: boolean; vendor: VendorStatusChange }>(`/api/vendors/${seg(vendorId)}/ctpp`, body);

export const listVendorAliases = (vendorId: string, options?: ApiOptions) =>
  apiGet<{ ok: boolean; vendor_id: string; aliases: VendorAlias[] }>(
    `/api/vendors/${seg(vendorId)}/aliases`,
    options,
  );

/**
 * Add one alias. `routable` is a separate decision from recognising the name:
 * it is what lets FlashText route evidence on it. A normalized alias owned by
 * another vendor comes back as a 409 — merge the vendors instead.
 */
export const addVendorAlias = (
  vendorId: string,
  body: { alias: string; routable?: boolean; actor?: string },
) =>
  apiPost<{ ok: boolean; alias: VendorAliasWrite; created: boolean }>(
    `/api/vendors/${seg(vendorId)}/aliases`,
    body,
  );

export const removeVendorAlias = (vendorId: string, aliasId: string) =>
  apiDelete<{ ok: boolean; removed: VendorAliasRemoval }>(
    `/api/vendors/${seg(vendorId)}/aliases/${seg(aliasId)}`,
  );

/** Re-read the designated CTPP list and stamp matching vendors (provenance `seed`). */
export const seedCtppEligibility = () =>
  apiPost<{ ok: boolean; vendors_updated: number }>("/api/vendors/ctpp/seed", {});

/**
 * Confirm provisional vendors named in a delivery role by N independent
 * sources. With no threshold and ORION1_VENDOR_CONFIRM_MIN_SOURCES unset this
 * confirms nothing and returns 0 — that is the documented default, not a bug.
 */
export const autoConfirmVendors = (minIndependentSources?: number) =>
  apiPost<{ ok: boolean; vendors_confirmed: number }>("/api/vendors/auto-confirm", {
    min_independent_sources: minIndependentSources ?? null,
  });

/* ----------------------------------------------------------------- merges -- */

export type VendorMergeResponse = {
  ok: boolean;
  merge: VendorMergeResult;
  /** Projects the merge refused to re-home; an operator must act on them. */
  conflicts: VendorMergeConflict[];
  conflict_count: number;
  projects_moved: number;
};

export const mergeVendors = (body: {
  survivor_vendor_id: string;
  absorbed_vendor_id: string;
  actor?: string;
}) => apiPost<VendorMergeResponse>("/api/merges/vendors", body);

export const listVendorMerges = (
  params: { survivor_vendor_id?: string; limit?: number; offset?: number } = {},
  options?: ApiOptions,
) =>
  apiGet<{
    ok: boolean;
    merges: VendorMergeRecord[];
    total: number;
    limit: number;
    offset: number;
  }>(`/api/merges/vendors${qs(params)}`, options);

export const listProjectMerges = (
  params: {
    project_id?: string;
    include_reversed?: boolean;
    limit?: number;
    offset?: number;
  } = {},
  options?: ApiOptions,
) =>
  apiGet<{ ok: boolean; merges: MergeEvent[]; total: number; limit: number; offset: number }>(
    `/api/merges/projects${qs(params)}`,
    options,
  );

export const getProjectMerge = (mergeEventId: string, options?: ApiOptions) =>
  apiGet<{ ok: boolean; merge: MergeEvent }>(
    `/api/merges/projects/${seg(mergeEventId)}`,
    options,
  );

/* --------------------------------------------------------------- mentions -- */

export type MentionListResponse = {
  ok: boolean;
  cluster_id: string;
  project_id: string;
  status: string;
  mentions: InitiativeMention[];
  counts: Record<string, number>;
  total: number;
};

/** GET /api/mentions — one of cluster_id or project_id is required. */
export const listMentions = (
  params: { cluster_id?: string; project_id?: string; status?: string },
  options?: ApiOptions,
) => apiGet<MentionListResponse>(`/api/mentions${qs(params)}`, options);

/** The cluster page's own mention read (phase3 router). */
export const listClusterMentions = (clusterId: string, status = "", options?: ApiOptions) =>
  apiGet<{ ok: boolean; cluster_id: string; mentions: InitiativeMention[] }>(
    `/api/phase3/clusters/${seg(clusterId)}/mentions${qs({ status })}`,
    options,
  );

/** Chunks deferred, skipped or failed: evidence bought and not read. */
export const listClusterChunkIssues = (
  params: { cluster_id: string; status?: string; limit?: number },
  options?: ApiOptions,
) =>
  apiGet<{
    ok: boolean;
    cluster_id: string;
    status: string;
    chunks: EvidenceChunkIssue[];
    counts: Record<string, number>;
    total: number;
  }>(`/api/mentions/chunks${qs(params)}`, options);

export const getMention = (mentionId: string, candidateLimit?: number, options?: ApiOptions) =>
  apiGet<{
    ok: boolean;
    mention: InitiativeMention;
    cluster_id: string;
    target_company_id: string;
    candidates: MentionCandidate[];
    decision: ActiveDecision | null;
  }>(`/api/mentions/${seg(mentionId)}${qs({ candidate_limit: candidateLimit })}`, options);

/* --------------------------------------------------------- reconciliation -- */

export const listReconciliationDecisions = (
  params: {
    cluster_id?: string;
    mention_id?: string;
    valid_only?: boolean;
    limit?: number;
  },
  options?: ApiOptions,
) =>
  apiGet<{
    ok: boolean;
    cluster_id: string;
    mention_id: string;
    decisions: ReconciliationDecision[];
    total: number;
    flags: Record<string, boolean>;
  }>(`/api/reconciliation/decisions${qs(params)}`, options);

/**
 * Start a reconciliation run. Returns 202 with a `run_id`; feed it to
 * `pollJob`. Refused with 409 while ORION1_RECONCILE_V3 is off, and with 402
 * when the target ledger has no budget left.
 */
export const runReconciliation = (
  clusterId: string,
  body: { model?: string; job_id?: string } = {},
) =>
  apiPost<ReconciliationRunAccepted>(`/api/reconciliation/clusters/${seg(clusterId)}/run`, {
    model: body.model ?? "",
    job_id: body.job_id ?? "",
  });

/** Operator override for one mention; invalidates the prior live decision. */
export const setMentionOutcome = (
  mentionId: string,
  body: { cluster_id: string; outcome: string; project_id?: string; reasoning?: string },
) =>
  apiPost<{
    ok: boolean;
    decision: { decision_id: string; outcome: string; created_at: string };
    mention_id: string;
    project_id: string;
  }>(`/api/reconciliation/mentions/${seg(mentionId)}/outcome`, body);

export const reopenMentionDecision = (mentionId: string, reason = "") =>
  apiPost<{
    ok: boolean;
    reopened: { mention_id: string; reopened: number; reason: string };
  }>(`/api/reconciliation/mentions/${seg(mentionId)}/reopen`, { reason });

/* ------------------------------------------------------ unresolved routing -- */

export const listClusterUnresolved = (clusterId: string, options?: ApiOptions) =>
  apiGet<{ ok: boolean; unresolved: UnresolvedRouting[] }>(
    `/api/phase3/clusters/${seg(clusterId)}/unresolved`,
    options,
  );

export const resolveUnresolvedRouting = (
  unresolvedId: string,
  body: { status?: string; actor?: string } = {},
) =>
  apiPost<{ ok: boolean; unresolved: UnresolvedRoutingResolution }>(
    `/api/reconciliation/unresolved/${seg(unresolvedId)}/resolve`,
    { status: body.status ?? "resolved", actor: body.actor ?? "operator" },
  );

/** Answer a possible duplicate batch synthesis held; the next synthesis run re-reads its pages. */
export const decidePossibleDuplicate = (unresolvedId: string, verdict: "same" | "separate") =>
  apiPost<{ ok: boolean; unresolved: PossibleDuplicateDecision }>(
    `/api/reconciliation/unresolved/${seg(unresolvedId)}/duplicate`,
    { verdict, actor: "operator" },
  );

/* ------------------------------------------------------------ cluster spend -- */

/** `scope` says whether this is a cluster figure or the target total. */
export const clusterSpend = (clusterId: string, options?: ApiOptions) =>
  apiGet<{
    ok: boolean;
    spend: ClusterSpendSummary;
    scope: ClusterSpendSummary["scope"];
    attributed: boolean;
    note: string;
  }>(`/api/phase3/clusters/${seg(clusterId)}/spend`, options);

/* ------------------------------------------------------------------- gaps -- */

export const listGaps = (
  params: { cluster_id: string; include_closed?: boolean; limit?: number },
  options?: ApiOptions,
) =>
  apiGet<{
    ok: boolean;
    cluster_id: string;
    gaps: ResearchGap[];
    open: number;
    total: number;
    flags: Record<string, boolean>;
  }>(`/api/gaps${qs(params)}`, options);

/** Run the fixed detection catalogue. 409 while ORION1_GAP_RESEARCH is off. */
export const detectGaps = (clusterId: string) =>
  apiPost<{
    ok: boolean;
    cluster_id: string;
    gaps: ResearchGap[];
    detected: number;
    suppressed: number;
  }>("/api/gaps/detect", { cluster_id: clusterId });

/**
 * Plan bounded follow-up search for one gap. Nothing is executed: the queries
 * go to the next search run. A gap the planner declined comes back as a 409
 * whose body carries the `reason` (attempt limit, cooldown, no allowance).
 */
export const researchGap = (gapId: string, runAllowanceUsd?: number) =>
  apiPost<{
    ok: boolean;
    gap: ResearchGap;
    search_items: GapSearchItem[];
    queries: number;
    run_allowance_usd: number;
    job_id: string;
    detected: number;
    skipped: ResearchGapSkip[];
  }>(`/api/gaps/${seg(gapId)}/research`, {
    run_allowance_usd: runAllowanceUsd ?? null,
  });

export const closeGap = (gapId: string, body: { status?: string; stop_reason?: string } = {}) =>
  apiPost<{ ok: boolean; gap: ResearchGap }>(`/api/gaps/${seg(gapId)}/close`, {
    status: body.status ?? "answered",
    stop_reason: body.stop_reason ?? "",
  });

export const reopenGap = (gapId: string, body: { allowance_usd?: number; actor?: string } = {}) =>
  apiPost<{
    ok: boolean;
    reopened: { gap_id: string; status: string; attempts: number; allowance_usd: number };
    gap: ResearchGap;
  }>(`/api/gaps/${seg(gapId)}/reopen`, {
    allowance_usd: body.allowance_usd ?? 0,
    actor: body.actor ?? "operator",
  });

/* --------------------------------------------------------------- projects -- */

export const listProjects = (
  params: { target?: string; project_id?: string; limit?: number; offset?: number } = {},
  options?: ApiOptions,
) =>
  apiGet<{
    ok: boolean;
    projects: ProjectRecord[];
    project?: ProjectRecord;
    total: number;
    limit: number;
    offset: number;
  }>(`/api/projects${qs(params)}`, options);

export const getProject = (projectId: string, options?: ApiOptions) =>
  apiGet<{ ok: boolean; project: ProjectRecord }>(`/api/projects/${seg(projectId)}`, options);

/** Participants and delivering participants for one project (plan 5.4). */
export const listProjectParticipants = (projectId: string, options?: ApiOptions) =>
  apiGet<{
    ok: boolean;
    project_id: string;
    participants: ProjectParticipant[];
    delivering_participants: ProjectParticipant[];
    mentions: InitiativeMention[];
  }>(`/api/phase3/projects/${seg(projectId)}/participants`, options);

export const listProjectRevisions = (projectId: string, options?: ApiOptions) =>
  apiGet<{ ok: boolean; revisions: ProjectRevision[] }>(
    `/api/projects/${seg(projectId)}/revisions`,
    options,
  );

/** Pass `expected_revision` to make the write fail (409) on a concurrent edit. */
export const updateProjectData = (
  projectId: string,
  body: {
    target_company_id: string;
    project_data: Record<string, unknown>;
    cluster_id?: string;
    expected_revision?: number | null;
  },
) => apiPut<{ ok: boolean; project: ProjectUpdateResult }>(`/api/projects/${seg(projectId)}/data`, body);

/**
 * Restore the state one revision recorded, as a new forward revision.
 *
 * `POST /api/projects/{id}/revisions/{n}/revert` does the read, the scope
 * check and both writes under a single advisory lock, so the restore and the
 * audit row it produces cannot land apart. History is never erased: the undo
 * is itself the next revision.
 *
 * The argument is the revision whose recorded state (`after_data`) is
 * restored, not the change to undo. `target_company_id` is optional — the
 * route reads the project's own when it is omitted — so a project the desk
 * has no company id for is still revertible. Pass `expectedRevision` to make
 * the write fail with 409 on a concurrent edit.
 */
export const revertProjectRevision = (
  projectId: string,
  params: {
    revisionNo: number;
    targetCompanyId?: string;
    clusterId?: string;
    expectedRevision?: number | null;
  },
) =>
  apiPost<{ ok: boolean; revert: ProjectRevertResult }>(
    `/api/projects/${seg(projectId)}/revisions/${params.revisionNo}/revert`,
    {
      target_company_id: params.targetCompanyId ?? "",
      cluster_id: params.clusterId ?? "",
      expected_revision: params.expectedRevision ?? null,
    },
  );

export const mergeProjects = (body: {
  survivor_project_id: string;
  absorbed_project_id: string;
}) => apiPost<{ ok: boolean; merge: ProjectMergeResult }>("/api/projects/merge", body);

export const undoProjectMerge = (mergeEventId: string) =>
  apiPost<{ ok: boolean; undo: ProjectMergeUndo }>(
    `/api/projects/merge/${seg(mergeEventId)}/undo`,
  );

export const splitProject = (
  projectId: string,
  body: {
    target_company_id: string;
    mention_ids: string[];
    creation_key: string;
    project_data: Record<string, unknown>;
    cluster_id?: string;
    vendor_id?: string;
  },
) => apiPost<{ ok: boolean; split: ProjectSplitResult }>(`/api/projects/${seg(projectId)}/split`, body);

/** "Not the same as" / "same as" between two projects. */
export const setProjectPairDecision = (body: {
  project_a_id: string;
  project_b_id: string;
  verdict: string;
  reasoning?: string;
}) => apiPost<{ ok: boolean }>("/api/projects/pair-decisions", body);

/* --------------------------------------------------------- search queries -- */

/**
 * The Phase 1 query registry (`search_queries`) behind the Search page panel.
 *
 * Scope is `target` (a desk target label, resolved to its target_companies
 * row) or `job_id`; the route requires one of them. The read is deliberately
 * not gated on ORION1_SEARCH_V3: with the flag off nothing has registered a
 * query, so the honest answer is `{ queries: [], total: 0 }` — a 200 with an
 * empty list, never a 404 or a 409.
 */
export const listSearchQueries = (
  params: { target?: string; jobId?: string; clusterId?: string; limit?: number },
  options?: ApiOptions,
) =>
  apiGet<{ ok: boolean; queries: SearchQueryRecord[]; total: number }>(
    `/api/phase1/search-queries${qs({
      target: params.target ?? "",
      job_id: params.jobId ?? "",
      cluster_id: params.clusterId ?? "",
      limit: params.limit ?? 200,
    })}`,
    options,
  );

/* ------------------------------------------------------ roadmap discovery -- */

export const getProjectRoadmap = (projectId: string, options?: ApiOptions) =>
  apiGet<{ ok: boolean } & ProjectRoadmap>(`/api/projects/${seg(projectId)}/roadmap`, options);

export const planRoadmapEventResearch = (
  projectId: string,
  eventId: string,
  body: { status_refresh?: boolean; allowance_usd?: number } = {},
) =>
  apiPost<{
    ok: boolean;
    objective: ResearchObjective;
    search_items: GapSearchItem[];
  }>(`/api/projects/${seg(projectId)}/roadmap/${seg(eventId)}/plan-research`, body);

export const listOpportunities = (
  params: {
    target?: string;
    projectId?: string;
    includeDismissed?: boolean;
    includeResearch?: boolean;
  } = {},
  options?: ApiOptions,
) =>
  apiGet<{
    ok: boolean;
    target_company_id: string;
    hypotheses: OpportunityHypothesis[];
    generation?: LeadGenerationRecord | null;
    last_attempt?: LeadGenerationRecord | null;
    origin?: ProjectLeadOrigin | null;
  }>(
    `/api/opportunities${qs({
      target: params.target ?? "",
      project_id: params.projectId ?? "",
      include_dismissed: params.includeDismissed ?? true,
      include_research: params.includeResearch ?? false,
    })}`,
    options,
  );

/** A lead-generation job was accepted (202); poll `/api/jobs/{run_id}`. */
export type LeadJobAccepted = {
  ok: boolean;
  status: string;
  run_id: string;
  poll_url: string;
  model: string;
  projects: number;
  force: boolean;
};

/**
 * One project's leads. In model mode (the default) this is a background job
 * (202 + run_id); in rules mode the old synchronous answer comes back.
 */
export const generateOpportunities = (body: {
  project_id: string;
  target_company_id?: string;
  root_cycle_id?: string;
  force?: boolean;
  model?: string;
}) =>
  apiPost<
    Partial<LeadJobAccepted> & { ok: boolean; hypotheses?: OpportunityHypothesis[]; message?: string }
  >("/api/opportunities/generate", body);

export const getLeadSettings = (options?: ApiOptions) =>
  apiGet<LeadSettings>("/api/opportunities/settings", options);

export const saveLeadModel = (model: string) =>
  apiPut<LeadSettings>("/api/opportunities/settings", { model });

export const estimateLeadGeneration = (body: { project_ids: string[]; model?: string; force?: boolean }) =>
  apiPost<LeadGenerationEstimate>("/api/opportunities/generate/estimate", body);

export const generateLeadsBulk = (body: {
  project_ids: string[];
  model?: string;
  force?: boolean;
  /** Plan research for the projects' open leads when generation finishes. */
  send_to_search?: boolean;
}) => apiPost<LeadJobAccepted>("/api/opportunities/generate-bulk", body);

export const planLeadResearchBulk = (body: { project_ids?: string[]; hypothesis_ids?: string[] }) =>
  apiPost<{
    ok: boolean;
    planned: number;
    queries: number;
    by_target: { target_company_id: string; target_name: string; leads: number; queries: number }[];
    skipped: { hypothesis_id: string; reason: string }[];
  }>("/api/opportunities/plan-research-bulk", body, { timeoutMs: 300_000 });

export const listLeadSearchItems = (target: string, options?: ApiOptions) =>
  apiGet<LeadSearchItems>(`/api/opportunities/search-items${qs({ target })}`, options);

export const leadProjectSummaries = (projectIds: string[], options?: ApiOptions) =>
  apiPost<{ ok: boolean; summaries: Record<string, LeadProjectSummary> }>(
    "/api/opportunities/project-summaries",
    { project_ids: projectIds },
    options,
  );

export const getRelatedProjects = (projectId: string, options?: ApiOptions) =>
  apiGet<RelatedProjects>(`/api/projects/${seg(projectId)}/related`, options);

/** A background job's lite status; `result` carries lead-generation progress. */
export const getLeadJob = (runId: string, options?: ApiOptions) =>
  apiGet<{
    ok: boolean;
    run_id: string;
    status: string;
    error?: string;
    result?: LeadGenerationProgress;
  }>(`/api/jobs/${seg(runId)}?lite=true`, options);

export const cancelJob = (runId: string) =>
  apiPost<{ ok: boolean; run_id: string; status: string }>(`/api/jobs/${seg(runId)}/cancel`, {});

// Project families (plans/project-families.md): one classification pass per bank.

export const getFamilySettings = (options?: ApiOptions) =>
  apiGet<FamilySettings>("/api/families/settings", options);

/** The desk setting; PUT /api/workspace accepts it like reconciliation_model. */
export const saveFamilyModel = (model: string) =>
  apiPut<{ ok: boolean }>("/api/workspace", { family_model: model });

export const getFamilies = (target: string, options?: ApiOptions) =>
  apiGet<FamilyOverview>(`/api/families?${new URLSearchParams({ target })}`, options);

export const estimateFamilies = (body: { target: string; mode: string; model?: string }) =>
  apiPost<FamilyEstimate>("/api/families/classify/estimate", body);

export const startFamilyClassification = (body: {
  target: string;
  mode: string;
  model?: string;
  dry_run: boolean;
}) => apiPost<FamilyRunAccepted>("/api/families/classify", body);

export const setFamilyPrefix = (target: string, prefix: string) =>
  apiPut<{ ok: boolean; target_company_id: string; prefix: string }>("/api/families/prefix", { target, prefix });

export const getFamilyJob = (runId: string, options?: ApiOptions) =>
  apiGet<{
    ok: boolean;
    run_id: string;
    status: string;
    error?: string;
    result?: FamilyRunProgress;
  }>(`/api/jobs/${seg(runId)}?lite=true`, options);

export const planOpportunityResearch = (hypothesisId: string, allowanceUsd = 0) =>
  apiPost<{ ok: boolean; objective: ResearchObjective; search_items: GapSearchItem[] }>(
    `/api/opportunities/${seg(hypothesisId)}/plan-research`,
    { allowance_usd: allowanceUsd },
  );

export const decideOpportunity = (
  hypothesisId: string,
  body: { action: string; matched_project_id?: string; reason?: string; actor?: string },
) =>
  apiPost<{ ok: boolean; hypothesis: OpportunityHypothesis }>(
    `/api/opportunities/${seg(hypothesisId)}/decision`,
    body,
  );

export const listResearchObjectives = (
  params: { target?: string; projectId?: string; eventId?: string; hypothesisId?: string } = {},
  options?: ApiOptions,
) =>
  apiGet<{ ok: boolean; objectives: ResearchObjective[] }>(
    `/api/research-objectives${qs({
      target: params.target ?? "",
      project_id: params.projectId ?? "",
      event_id: params.eventId ?? "",
      hypothesis_id: params.hypothesisId ?? "",
    })}`,
    options,
  );

/* ------------------------------------------------------------------ spend -- */

export const currentSpend = (jobId = "", options?: ApiOptions) =>
  apiGet<{ ok: boolean; spend: JobSpendSnapshot }>(`/api/spend/current${qs({ job_id: jobId })}`, options);

export const spendSummary = (jobId: string, runId = "", options?: ApiOptions) =>
  apiGet<{ ok: boolean; summary: SpendStageSummary }>(
    `/api/spend/summary${qs({ job_id: jobId, run_id: runId })}`,
    options,
  );

/**
 * Per-run stage caps with env var, default and provenance. Without `jobId`
 * this is a configuration read: `used`/`remaining` come back null because
 * nothing was counted — which is not the same as zero.
 */
export const stageCapReport = (jobId = "", runId = "", options?: ApiOptions) =>
  apiGet<{
    ok: boolean;
    job_id: string;
    run_id: string;
    usage_scope: "run" | "job" | "config";
    caps: StageCapReport[];
  }>(`/api/spend/stage-caps${qs({ job_id: jobId, run_id: runId })}`, options);

export const listRunAllowances = (
  params: { job_id?: string; run_id?: string; limit?: number },
  options?: ApiOptions,
) =>
  apiGet<{ ok: boolean; job_id: string; run_id: string; allowances: RunAllowance[] }>(
    `/api/spend/run-allowances${qs(params)}`,
    options,
  );

export const listBudgetAdjustments = (jobId: string, limit?: number, options?: ApiOptions) =>
  apiGet<{ ok: boolean; job_id: string; adjustments: BudgetAdjustment[] }>(
    `/api/spend/budget-adjustments${qs({ job_id: jobId, limit })}`,
    options,
  );

/** Raise or cut the target ledger. `reason` is required and audited. */
export const adjustBudget = (body: {
  job_id: string;
  budget_usd: number;
  reason: string;
  actor?: string;
}) => apiPost<{ ok: boolean; spend: JobSpendSnapshot }>("/api/spend/budget-adjustments", body);
